import React, { useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import BreadcrumbsSection from "../BreadcrumbsSection"
import { showNotification } from "aldoo-ra/Notification"
import { SendHelpCenterRequest } from "../api"
import TopRightPageBg from "../../Site/TopRightPageBg"
import Input from "aldoo-ra/Input"
import { ChevronDownIcon, CheckIcon, PaperClipIcon, XMarkIcon } from "@heroicons/react/24/outline"
import DropZone from "aldoo-ra/DropZone"
import Icon from "aldoo-ra/Icon"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import CMSData from "aldoo-ra/CMS/cms-data" // Add this import
import Value from "aldoo-ra/Value"
import { motion } from "framer-motion"

/**
 * SubmitRequestPage Component
 * Provides a form for users to submit help requests
 */
const SubmitRequestPage = () => {

  // Use direct Value hook for site language (not help center override)
  const [currentLanguage] = Value({ 
    key: "current-site-language", 
    defaultValue: "en",
    persistKey: "current-site-language",
  })
  const texts = TextBundle({ id: "hc_submit-request" })

  // Fetch product categories from CMS
  const { data: categoryData, loading: categoriesLoading, error: cmsError } = CMSData({
    contentType: "CSRequestCategory",
    match: {
      "content.id": "submit-ticket",
      "content.type": "CSRequestCategory",
    },
    language: currentLanguage,
  })

  // Extract product options from CMS data
  const productOptions =
    categoryData && categoryData.length > 0 ? categoryData[0]?.pairs || {} : {}

  // Form field states
  const [email, setEmail] = useState("")
  const [product, setProduct] = useState("")
  const [description, setDescription] = useState("")
  const [agreed, setAgreed] = useState(false)
  const [files, setFiles] = useState([])

  // Form validation and submission states
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  // Breadcrumbs configuration
  const breadcrumbItems = [
    {
      label: texts?.help_center || "Help Center",
      link: "/help-center",
      onClick: () => {
        // Use history.pushState for SPA navigation instead of full page reload
        window.history.pushState(null, "", "/help-center")
        // Trigger custom navigation event to notify other components
        window.dispatchEvent(new CustomEvent('helpCenterNavigate'))
        // Also trigger popstate for browser compatibility
        window.dispatchEvent(new PopStateEvent('popstate'))
      },
    },
    { label: texts?.submit_request || "Submit a Request" },
  ]

  // Form field handlers
  const handleEmailChange = (e) => {
    setEmail(e.target.value)
    // Clear error when user starts typing
    if (errors.email) {
      setErrors({ ...errors, email: null })
    }
  }

  const handleProductChange = (e) => {
    setProduct(e.target.value)
    // Clear error when user makes a selection
    if (errors.product) {
      setErrors({ ...errors, product: null })
    }
  }

  const handleDescriptionChange = (e) => {
    setDescription(e.target.value)
    // Clear error when user starts typing
    if (errors.description) {
      setErrors({ ...errors, description: null })
    }
  }

  const handleAgreementChange = (e) => {
    setAgreed(e.target.checked)
  }

  // Handle file drop from DropZone
  const handleFileDrop = (acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const newFiles = [...files, ...acceptedFiles].slice(0, 5) // Limit to 5 files
      setFiles(newFiles)

      // Clear file error if it exists
      if (errors.file) {
        setErrors({ ...errors, file: null })
      }
    }
  }

  // Handle file removal
  const handleRemoveFile = (index) => {
    const updatedFiles = [...files]
    updatedFiles.splice(index, 1)
    setFiles(updatedFiles)
  }

  // Form validation
  const validateForm = () => {
    const errors = {}
    if (!email.trim()) errors.email = texts?.enter_email || "Enter email"
    if (!product)
      errors.product = texts?.select_product || "Please select a product."

    // Check description more carefully - it might have text that's just whitespace
    if (!description || !description.trim()) {
      errors.description =
        texts?.provide_description || "Please provide a description."
    }

    if (!agreed)
      errors.agreed = texts?.agree_to_terms || "You must agree to the terms."

    // Check file size if files are selected
    const oversizedFiles = files.filter((file) => file.size > 20 * 1024 * 1024)
    if (oversizedFiles.length > 0) {
      errors.file = texts?.file_size || "File size must be less than 20MB."
    }

    return errors
  }

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setSubmitError(null)


    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }
    setErrors({})

    setIsSubmitting(true)
    try {
      // Convert product to tags array
      const tags = product ? [product] : []

      // Ensure description is properly trimmed before sending
      const payload = {
        email: email.trim(),
        tags, // Use tags array instead of product field
        description: description.trim(),
        files: files.length > 0 ? files : null,
      }


      const response = await SendHelpCenterRequest(payload)

      if (response.error) {
        // Handle specific error codes
        if (response.error === "no_email") {
          setErrors({
            ...errors,
            email: texts?.no_email_error || "Email is required.",
          })
        } else if (response.error === "no_tags") {
          // Update error handling
          setErrors({
            ...errors,
            product:
              texts?.no_product_error || "Product selection is required.",
          })
        } else if (response.error === "no_description") {
          setErrors({
            ...errors,
            description:
              texts?.no_description_error || "Description is required.",
          })
        } else if (response.error === "file_too_big") {
          setErrors({
            ...errors,
            file: texts?.file_size_error || "File size must be less than 20MB.",
          })
        } else if (response.error === "email_send_failed") {
          setSubmitError(
            texts?.send_email_error ||
              "Failed to send email. Please try again later."
          )
        } else if (response.error === "protection_error") {
          // Handle the protection error with a red notification
          showNotification({
            message:
              texts?.message_protection_error ||
              "Message Protection error. Please revise your message or email",
            className: "bg-error text-text_inv",
          })
        } else {
          setSubmitError(`Server error: ${response.error}`)
        }
        return
      }

      if (response.result) {
        showNotification({
          message: "Thank you! Your request will be reviewed as soon as possible.",
          onRender: ({ closeNotification, message, variants }) => (
            <motion.div
              className="relative flex items-center justify-between text-text bg-primary_lt rounded-[16px] md:rounded-full mt-[64px] mx-4"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Notification text + Check icon */}
              <div className="flex w-[full] max-w-[550px] md:w-fit md:max-w-fit items-start md:items-center px-5 py-2">
                <Icon className="w-6 h-6 mr-2" id="check_mark_icon" />

                <Typography variant="body1" className="my-0">
                  {message}
                </Typography>
              </div>

              {/* Close button */}
              <button
                onClick={closeNotification}
                className="absolute w-[22px] h-[22px] -top-2 right-0 flex items-center justify-center text-gray_700 hover:text-text bg-gray_200 rounded-full transition-colors"
                aria-label="Close notification"
              >
                <XMarkIcon className="h-4 w-4" strokeWidth={2} />
              </button>
            </motion.div>
          ),
        })

        // Reset form
        setEmail("")
        setProduct("")
        setDescription("")
        setAgreed(false)
        setFiles([])
      }
    } catch (error) {
      setSubmitError(
        texts?.submit_error ||
          "An error occurred while submitting your request. Please try again."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="relative bg-gradient_hc_home min-h-screen rounded-b-[50px] md:rounded-b-[100px] pb-20 md:pb-[140px] overflow-x-hidden">
      <div className="w-full md:max-w-[705px] lg:max-w-[734px] px-4 mx-auto">
        <BreadcrumbsSection items={breadcrumbItems} />

        <Typography
          variant="h1"
          className="text-center md:text-left mb-5 md:mb-7"
        >
          {texts?.submit_request || "Submit a request"}
        </Typography>

        <div className="mb-5 md:mb-14">
          <Typography
            variant="h4_light"
            className="text-text mt-0 mb-5 md:mb-14"
          >
            {texts?.subtitle ||
              "If you have any questions, comments, or need to report an issue,don't hesitate to contact us — we're here to help!"}
          </Typography>

          <Typography variant="body1_dim">
            {texts?.note ||
              "Before messaging, please check our articles for answers to common questions - you might find what you need right away."}
          </Typography>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-5 md:gap-[30px] max-w-[734px]"
        >
          {/* Email Field */}
          <div>
            <label
              className={`block mb-2 ${
                errors.email ? "text-error" : "text-text"
              }`}
            >
              <Typography variant="body1" className="mt-0 mb-6">
                {texts?.email_label || "Your email*"}
              </Typography>
            </label>

            <Input
              type="email"
              variant="mobilingo_standard"
              value={email}
              onChange={handleEmailChange}
              placeholder={texts?.email_placeholder || "Email address"}
              className={`w-full h-[59px] text-[16px] md:text-[17px] lg:text-[20px] placeholder:text-gray_700 ${
                errors.email ? "border-error" : "border-bordered"
              } `}
            />

            {errors.email && (
              <Typography variant="error">{errors.email}</Typography>
            )}
          </div>

          {/* Product Field */}
          <div>
            <label
              className={`block mb-2 ${
                errors.product ? "text-error" : "text-text"
              }`}
            >
              <Typography variant="body1" className="mt-0 mb-6">
                {texts?.product_label || "Product*"}
              </Typography>
            </label>

            <div className="relative">
              <Typography variant={product ? "body1" : "body1_dim"}>
                <select
                  value={product}
                  onChange={handleProductChange}
                  className={`appearance-none w-full h-[59px] bg-gray_100 border ${
                    errors.product ? "border-error" : "border-bordered"
                  } rounded-full focus:outline-none focus:ring-1 focus:ring-primary pl-7 pr-10`}
                >
                  <option value="">
                    {texts?.select_product || "Select product"}
                  </option>
                  {/* Replace hardcoded options with dynamic options from CMS */}
                  {Object.entries(productOptions).map(([key, label]) => (
                    <option key={key} value={key}>
                      {texts?.[key] || label}
                    </option>
                  ))}
                </select>
              </Typography>

              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4">
                <ChevronDownIcon className="w-5 h-5 text-gray_700" />
              </div>
            </div>
            {errors.product && (
              <Typography variant="error">{errors.product}</Typography>
            )}
          </div>

          {/* Description Field */}
          <div>
            <label
              className={`block mb-2 ${
                errors.description ? "text-error" : "text-text"
              }`}
            >
              <Typography variant="body1" className="mt-0 mb-6">
                {texts?.description_label || "Description*"}
              </Typography>
            </label>

            <textarea
              value={description}
              onChange={handleDescriptionChange}
              placeholder={
                texts?.description_placeholder ||
                "Include as much detail as you can including operating platform, device model etc."
              }
              className={`w-full outline-none text-[16px] md:text-[17px] lg:text-[20px] text-text placeholder:text-gray_700 bg-gray_100 border ${
                errors.description ? "border-error" : "border-bordered"
              } rounded-[30px] focus:outline-none focus:ring-1 focus:ring-primary p-4 pl-7`}
              rows="5"
              data-testid="description-input"
            ></textarea>

            {errors.description && (
              <Typography variant="error" data-testid="description-error">
                {errors.description}
              </Typography>
            )}

            <Typography variant="txt1_dim" className="my-0">
              {texts?.description_note ||
                "Please include details of your request (steps to reproduce, device, etc) so we can provide the most complete and accurate support. If relevant, screenshots are highly encouraged."}
            </Typography>
          </div>

          {/* File Attachment - Using DropZone with custom attachment display */}
          <div>
            <Typography
              variant="body1"
              className="block mt-0 mb-6
            "
            >
              {texts?.attachments_label || "Attachments (optional)"}
            </Typography>

            <div className="w-full">
              {/* Only show dropzone if no files or multiple files allowed */}
              {(files.length === 0 || files.length < 5) && (
                <DropZone
                  onDrop={handleFileDrop}
                  maxFiles={5 - files.length}
                  accept={{
                    "image/*": [".jpg", ".jpeg", ".png", ".gif"],
                    "application/pdf": [".pdf"],
                    "application/msword": [".doc", ".docx"],
                    "text/plain": [".txt"],
                  }}
                  onRender={({
                    isDragging,
                    handleClick,
                    handleDragEnter,
                    handleDragOver,
                    handleDragLeave,
                    handleDrop,
                  }) => (
                    <div
                      onClick={handleClick}
                      onDragEnter={handleDragEnter}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`
                        w-full
                        rounded-[30px]
                        border
                        ${
                          isDragging
                            ? "border-primary bg-gray_50"
                            : "border-bordered bg-white"
                        }
                        p-6
                        flex
                        justify-center
                        items-center
                        h-[140px]
                        transition-all
                        duration-200
                        cursor-pointer
                        hover:border-primary
                        hover:bg-gray_50
                      `}
                    >
                      <div className="text-center">
                        <span className="text-primary">
                          {texts?.dropzone_add_file || "Add file"}
                        </span>
                        <span className="text-gray_700">
                          {" "}
                          {texts?.dropzone_drop_files || "or drop files here"}
                        </span>
                      </div>
                    </div>
                  )}
                />
              )}

              {/* Display selected attachments */}
              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="bg-primary_lt rounded-full py-2 px-4 flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <PaperClipIcon className="h-5 w-5 text-gray-700 mr-2" />

                        <Typography variant="body1_dim" className=" my-0">
                          {file.name}
                        </Typography>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveFile(index)}
                        className="text-gray_500 hover:text-gray_700 transition-colors"
                        aria-label="Remove file"
                      >
                        <XMarkIcon className="h-7 w-7 stroke-2" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {errors.file && (
                <Typography variant="error">{errors.file}</Typography>
              )}
            </div>
          </div>

          {/* Terms Agreement */}
          <div>
            <label
              className={`flex items-start  ${
                errors.agreed ? "text-error" : "text-text"
              }`}
            >
              <input
                type="checkbox"
                checked={agreed}
                onChange={handleAgreementChange}
                className="accent-primary mt-[6px] lg:mt-[9px] mr-2"
              />

              <Typography variant="body1" className="my-0">
                {texts?.terms_agreement || "I've read and agree to MobiLingo's"}{" "}
                <a
                  href="/terms-of-service"
                  className="text-primary hover:underline"
                >
                  {texts?.tc || "Terms of Service"}
                </a>{" "}
                and{" "}
                <a
                  href="/privacy-policy"
                  className="text-primary hover:underline"
                >
                  {texts?.pp || "Privacy Policy"}
                </a>
              </Typography>
            </label>

            {errors.agreed && (
              <Typography variant="error">{errors.agreed}</Typography>
            )}
          </div>

          {/* Submit Button */}
          <div>
            <Button
              type="submit"
              variant="primary_l"
              className="min-w-[214px] rounded-full"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? texts?.request_sending || "Sending..."
                : texts?.request_send || "Send"}
            </Button>

            {Object.keys(errors).length > 0 && (
              <Typography variant="error">
                {texts?.correct_fields ||
                  "Please correct the highlighted fields above."}
              </Typography>
            )}

            {submitError && (
              <Typography variant="error">{submitError}</Typography>
            )}
          </div>
        </form>
      </div>

      <TopRightPageBg />
    </div>
  )
}

export default SubmitRequestPage
